import { VERTICALS_BY_CODE, verticalCopy } from '@/lib/verticals';
import { useT, useRaw } from '@/lib/i18n';
import { ExternalLink } from 'lucide-react';

/**
 * Carte "Application réelle OpenMeta".
 *
 * - Si `app.screenshot` est défini : affiche un aperçu réel du site
 *   dans un léger cadre navigateur (chrome bar avec les 3 pastilles + url).
 * - Sinon : conserve l'ancienne vignette « illustration + monogramme »
 *   (fallback pour toute app dont on n'a pas encore le screenshot).
 *
 * Récupère les copies dans i18n `applications_data.<id>`.
 */
export const AppShowcaseCard = ({ app, dataTestId }) => {
  const t = useT();
  const raw = useRaw();
  const v = VERTICALS_BY_CODE[app.verticalCode];
  const vc = verticalCopy(raw, app.verticalCode);
  const illustration = v?.illustration || null;
  const copy = raw(`applications_data.${app.id}`) || {};
  const tagline = copy.tagline || '';
  const description = copy.description || '';
  const displayHost = app.url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <a
      href={app.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={dataTestId || `app-card-${app.id}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-6 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_20px_40px_-30px_rgba(10,14,26,0.18)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(10,14,26,0.3)]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-60 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: `radial-gradient(closest-side, ${app.color}55, transparent 70%)` }}
      />

      {app.screenshot ? (
        <AppScreenshotFrame
          src={app.screenshot}
          alt={`${app.name} — ${tagline || displayHost}`}
          host={displayHost}
          accent={app.color}
          dataTestId={`app-screenshot-${app.id}`}
        />
      ) : (
        <div className="relative flex h-48 items-center justify-center overflow-hidden rounded-2xl om-illust-frame bg-white">
          <div
            aria-hidden
            className="absolute inset-0 opacity-70"
            style={{ background: `radial-gradient(circle at 30% 20%, ${app.color}22, transparent 70%)` }}
          />
          {illustration && (
            <img
              src={illustration}
              alt=""
              aria-hidden
              width={300}
              height={300}
              loading="lazy"
              className="relative h-40 w-40 object-contain opacity-90 transition-transform duration-500 group-hover:scale-105"
              draggable={false}
            />
          )}
          <div
            className="absolute bottom-3 right-3 grid h-14 w-14 place-items-center rounded-2xl text-2xl font-extrabold"
            style={{
              background: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(14px) saturate(1.1)',
              WebkitBackdropFilter: 'blur(14px) saturate(1.1)',
              border: '1px solid rgba(10,14,26,0.06)',
              color: app.color,
              boxShadow: '0 10px 24px -12px rgba(10,14,26,0.2)',
            }}
            data-testid={`app-monogram-${app.id}`}
          >
            {app.initial}
          </div>
        </div>
      )}

      <div className="relative mt-5 flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
          style={{ color: app.color, background: `${app.color}18` }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: app.color }} />
          {vc.short}
        </span>
      </div>

      <h3 className="relative mt-3 text-[20px] font-bold tracking-tight text-[var(--om-ink)]">
        {app.name}
      </h3>
      <p className="relative mt-1 text-sm font-medium text-[var(--om-ink-soft)]">
        {tagline}
      </p>
      <p className="relative mt-3 text-sm leading-relaxed text-[var(--om-muted)]">
        {description}
      </p>

      <div className="relative mt-6 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--om-ink)]">
          {t('applications_page.visit_app')}
          <ExternalLink size={14} strokeWidth={2.4} />
        </span>
        <span className="text-xs text-[var(--om-muted-2)] truncate max-w-[45%]" title={app.url}>
          {displayHost}
        </span>
      </div>
    </a>
  );
};

/**
 * Cadre "navigateur" stylisé pour héberger un screenshot 16:10.
 * Chrome bar minimal (3 pastilles + url tronquée), ombres douces,
 * transition zoom au hover.
 */
function AppScreenshotFrame({ src, alt, host, accent, dataTestId }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg)]"
      style={{ aspectRatio: '16 / 10' }}
      data-testid={dataTestId}
    >
      {/* Chrome bar */}
      <div className="flex items-center gap-1.5 border-b border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-[#FF605C]" aria-hidden />
        <span className="h-2 w-2 rounded-full bg-[#FFBD44]" aria-hidden />
        <span className="h-2 w-2 rounded-full bg-[#00CA4E]" aria-hidden />
        <span
          className="ml-2 truncate rounded-full bg-[var(--om-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--om-muted-2)]"
          style={{ maxWidth: '75%' }}
        >
          {host}
        </span>
      </div>
      {/* Screenshot */}
      <div className="relative h-[calc(100%-30px)] overflow-hidden">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
          draggable={false}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: `linear-gradient(180deg, transparent 60%, ${accent}12 100%)` }}
        />
      </div>
    </div>
  );
}
